<template>
    <kt-modal
        :visible="true"
        title="Recent Requests for Customer"
        size="modal-lg"
        @dismiss="emit('close')"
    >
        <template #body>
            <div v-if="state.IsLoading" class="text-center py-3">
                <i class="fas fa-spinner fa-spin me-2"></i>Loading recent requests...
            </div>

            <div v-else-if="state.IsError" class="alert alert-danger py-2">
                {{ state.Message || 'Failed to load recent requests' }}
            </div>

            <div v-else-if="requests.length === 0" class="text-muted text-center py-3">
                No previous requests found for this customer.
            </div>

            <div v-else class="table-responsive" style="max-height: 340px; overflow-y: auto;">
                <table class="table table-sm table-striped table-hover mb-0">
                    <thead>
                        <tr>
                            <th style="width: 70px;">ID</th>
                            <th style="width: 110px;">Created</th>
                            <th>Title</th>
                            <th style="width: 140px;">Requestor</th>
                            <th style="width: 110px;">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="req in requests"
                            :key="req.Id"
                            style="cursor: pointer;"
                            @click="openRequest(req.Id)"
                            title="Open this request"
                        >
                            <td>{{ req.Id }}</td>
                            <td>{{ req.DateCreated || '--' }}</td>
                            <td>{{ req.RequestTitle || '--' }}</td>
                            <td>{{ req.Requestor || '--' }}</td>
                            <td>
                                <span class="badge" :class="statusBadgeClass(req.Status)">
                                    {{ statusLabel(req.Status) }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
        <template #footer>
            <div class="kt-modal-actions">
                <button @click="emit('close')" class="btn btn-outline-secondary">
                    <X :size="16" class="me-1" />Close
                </button>
            </div>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import KtModal from "@/components/UI/KtModal.vue";
import { useApiRequest } from "@/composables";
import { useRequestStatusStore } from "@/stores/RequestStatusStore";
import type { WorkOrderModel } from "@/models/WorkOrderModel";
import { X } from "lucide-vue-next";

const props = defineProps({
    customerNumber: {
        type: [Number, String],
        required: true
    },
    excludeId: {
        type: [Number, String],
        default: null
    }
});

const emit = defineEmits(["close"]);

const requestStatusStore = useRequestStatusStore();
const requests = ref<WorkOrderModel[]>([]);
const { state, execute } = useApiRequest<WorkOrderModel[]>("request:history");

const statusLabel = (status?: number | null) => {
    if (status == null) return "--";
    return requestStatusStore.getByValue(status)?.Label || `Status ${status}`;
};

const statusBadgeClass = (status?: number | null) => {
    const label = statusLabel(status).toLowerCase();
    if (label.includes("progress")) return "bg-primary";
    if (label.includes("complete")) return "bg-success";
    if (label.includes("hold")) return "bg-warning text-dark";
    if (label.includes("declin") || label.includes("cancel")) return "bg-danger";
    return "bg-secondary";
};

const openRequest = (id?: number | null) => {
    if (!id) return;
    Emitter.emit("edit:job", id);
    emit("close");
};

onMounted(async () => {
    // Fetch one extra row so the current request can be excluded client-side
    const result = await execute(() => ApiService.loadWorkOrders({
        Title: "Request",
        ContactId: [Number(props.customerNumber)],
        Page: 0,
        Size: 11
    }));

    const rows = (result.Data as WorkOrderModel[]) || [];
    const excludeId = props.excludeId ? Number(props.excludeId) : null;
    requests.value = rows
        .filter(r => !excludeId || r.Id !== excludeId)
        .slice(0, 10);
});
</script>
